"use client"

import { Button } from "@/components/ui/button"
import { forwardRef, useRef, useState } from "react"

interface FileInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "className"> {
  className?: string
  buttonText?: string
}

export const FileInput = forwardRef<HTMLInputElement, FileInputProps>(
  (
    { className, buttonText = "Choose File", onChange, disabled, ...props },
    ref
  ) => {
    const [fileName, setFileName] = useState<string>("")
    const inputRef = useRef<HTMLInputElement>(null)

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      setFileName(file ? file.name : "")

      if (onChange) {
        onChange(e)
      }
    }

    return (
      <div className={`flex items-center gap-3 ${className}`}>
        <Button
          type="button"
          variant="outline"
          className="cursor-pointer"
          disabled={disabled}
          onClick={() => inputRef.current?.click()}
        >
          {buttonText}
        </Button>

        <span className="text-sm text-muted-foreground">
          {fileName || "No file chosen"}
        </span>

        <input
          type="file"
          className="hidden"
          ref={(node) => {
            // Handle the forwarded ref first
            if (typeof ref === "function") {
              ref(node)
            } else if (ref) {
              // This is safe because React guarantees the ref object's shape
              ;(
                ref as React.MutableRefObject<HTMLInputElement | null>
              ).current = node
            }

            // Then handle our own internal ref
            ;(
              inputRef as React.MutableRefObject<HTMLInputElement | null>
            ).current = node
          }}
          onChange={handleFileChange}
          disabled={disabled}
          {...props}
        />
      </div>
    )
  }
)

FileInput.displayName = "FileInput"
